<script setup lang="ts">
import type { DtwinMetricsCounts } from "../models/DtwinMetricsCounts.type";
import { Filters } from "~/core/models/filters";
import { useFetchDtwinMetricsFirmwareVersion } from "~/modules/dashboard/composables/useFetchDtwinMetricsFirmwareVersion";

const props = defineProps<{
  organizationCode?: number;
  selectedSectorCodes?: number[];
  userAssignedSectorsCodes: number[];
}>();

const { t } = useI18n();

const { isLoading, error, result, fetchData, setFilter } =
  useFetchDtwinMetricsFirmwareVersion({
    organizationCode: props.organizationCode,
  });

const selectedSectorsComputed = computed(() => {
  if (props.selectedSectorCodes && props.selectedSectorCodes.length > 0) {
    return props.selectedSectorCodes.map((sectorCode) => sectorCode.toString());
  } else {
    return props.userAssignedSectorsCodes;
  }
});

async function loadData() {
  setFilter(Filters.SECTORS, selectedSectorsComputed.value);
  await fetchData();
}

const resultWithMetricsCounts = computed(() => {
  const resultWithMetricsCounts: Map<string, DtwinMetricsCounts> = new Map();

  if (result.value) {
    for (const [modelFriendlyName, dtwinMetricsCounts] of Object.entries(
      result.value,
    )
      // sort model friendly name in alphabetic order
      .sort((entry1, entry2) => entry1[0].localeCompare(entry2[0]))) {
      if (Object.values(dtwinMetricsCounts).length > 0) {
        resultWithMetricsCounts.set(modelFriendlyName, dtwinMetricsCounts);
      }
    }
  }

  return resultWithMetricsCounts;
});

watch(
  () => selectedSectorsComputed.value,
  async () => await loadData(),
);

onMounted(loadData);
</script>

<template>
  <div class="col-span-2">
    <dashboard-card
      :title="t('dashboard.firmwares.title')"
      :is-loading="isLoading"
      :error="error"
      loader-container-class="mt-8 mb-8"
    >
      <div
        v-if="resultWithMetricsCounts.size > 0"
        class="grid lg:grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-x-32 gap-y-8"
      >
        <dashboard-dtwin-firmware-version-chart
          v-for="[
            modelFriendlyName,
            dtwinMetricsCounts,
          ] of resultWithMetricsCounts"
          :key="modelFriendlyName"
          :dtwin-metrics-counts="dtwinMetricsCounts"
          :dtwin-model-friendly-name="modelFriendlyName"
        />
      </div>
      <div v-else>
        <h6 class="text-gray-700">{{ t("dashboard.firmwares.noData") }}</h6>
      </div>
    </dashboard-card>
  </div>
</template>
